import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, MailPlus } from "lucide-react";
import { mailLabel } from "@/components/MailBadge";
import { MailingAddressReview } from "@/components/MailingAddressReview";

const Side = ({ html, label, testId }) => (
  <div>
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="relative w-full overflow-hidden rounded-md border border-slate-200 shadow-sm" style={{ aspectRatio: "3 / 2" }}>
      <iframe title={label} srcDoc={html} sandbox="" data-testid={testId}
        className="absolute left-0 top-0 origin-top-left border-0" style={{ width: "576px", height: "384px", transform: "scale(var(--s))" }}
        ref={(el) => { if (el) el.style.setProperty("--s", el.parentElement.clientWidth / 576); }} />
    </div>
  </div>
);

export const PostcardButton = ({ prospect, disabled, onSent, price }) => {
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(null);
  const [sending, setSending] = useState(false);
  const openPreview = async () => {
    setOpen(true); setPv(null);
    try { setPv((await api.get(`/prospects/${prospect.id}/postcard-preview`)).data); }
    catch (e) { toast.error(apiError(e)); setOpen(false); }
  };
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/payments/contact-checkout", { prospect_id: prospect.id, action: "postcard", origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Postcard sent for mailing");
      setOpen(false); onSent?.();
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || apiError(e)); }
    setSending(false);
  };
  const sent = !!prospect?.postcard_sent_at;
  return (
    <>
      <Button onClick={openPreview} disabled={disabled} variant="outline" className="col-span-2 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="drawer-postcard-btn">
        <MailPlus className="mr-1.5 h-4 w-4" /> {sent ? `Postcard: ${mailLabel(prospect.mail_status) || "sent"}` : `Send Postcard${price ? ` · $${price}` : ""}`}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="postcard-dialog">
          <DialogHeader>
            <DialogTitle>Postcard preview</DialogTitle>
            <DialogDescription>6×4 postcard, First Class, mailed to the property address.{price ? ` $${price} per postcard, paid at checkout.` : ""}</DialogDescription>
          </DialogHeader>
          {!pv ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Side html={pv.front_html} label="Front" testId="postcard-front" />
                <Side html={pv.back_html} label="Back (address block added by PostGrid)" testId="postcard-back" />
              </div>
              {pv.mailing && <MailingAddressReview key={JSON.stringify(pv.mailing)} prospectId={prospect.id} mailing={pv.mailing}
                onUpdated={(m) => setPv({ ...pv, mailing: { ...pv.mailing, ...m }, eligible: !pv.reason && ["verified", "corrected"].includes(m.status) })} />}
              {!pv.configured && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="postcard-not-configured">PostGrid isn't connected yet — an administrator needs to add the PostGrid API key.</div>}
              {pv.configured && !pv.eligible && pv.reason && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="postcard-ineligible">{pv.reason}</div>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)} data-testid="postcard-cancel-btn">Cancel</Button>
                <Button onClick={send} disabled={sending || !pv.configured || !pv.eligible} className="bg-slate-900 hover:bg-slate-800" data-testid="postcard-send-btn">
                  {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MailPlus className="mr-1.5 h-4 w-4" />} Mail this postcard{price ? ` · $${price}` : ""}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
